import {
  Alert,
  Box,
  Button,
  Card,
  HStack,
  Separator,
  Spacer,
  Stack,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { useMemo } from "react";
import { isScoreFiltersEmpty } from "../../../../map/models/MapFilters";
import { MultiContextPieChart } from "../pie-chart/MultiContextPieChart";
import { ColorBySelector } from "../ColorByLegend/ColorBySelector";
import { CraAttributeFilters } from "../filters/CraAttributeFilters/CraAttributeFilters";
import { CraScoreFilters } from "../filters/CraScoreFilters";
import { TreatmentFilters } from "../filters/TreatmentFilters";
import { CraForecastControls } from "../CraForecast/CraForecastControls";
import { LifeCycleStageFilters } from "../filters/LifeCycleStageFilters";
import { useStores } from "../../../../shared/stores/RootStore";
import { formatDistanceByUnit } from "../../../../shared/utils/format-utils";
import { LifeCycleStage } from "../../../../osaway/models/LifeCycleStage";
import { OsaWayFilters } from "../filters/OsaWayFilters";
import useAutoResizeMap from "../../../../hooks/useAutoResizeMap";
import { DistanceUnit } from "@keita/lanepatrol-sdk";
import { HlaScoreFilters } from "../filters/HlaScoreFilters";
import { HlaAttributeFilters } from "../filters/HlaAttributeFilters";
import { HlaVerifiedFilters } from "../filters/HlaVerifiedFilters";
import { HlaForecastControls } from "../HlaForecast/HlaForecastControls";

const FiltersBase: React.FC = () => {
  useAutoResizeMap();
  const {
    mapStore,
    filterStore,
    authStore,
    osaWayStore,
    hlaStore,
    projectStore,
  } = useStores();
  const selectedOsaWay = osaWayStore.getSelected();

  const nodes = filterStore.nodesAvailableInCurrentMode;
  const distanceUnit = projectStore.distanceUnit;

  const totalCraDistance = useMemo(() => {
    return [...nodes.values()].reduce(
      (totalLength, nodes) =>
        totalLength +
        nodes.reduce(
          (routeLength, node) => routeLength + node.distanceToNext,
          0,
        ),
      0,
    );
  }, [nodes]);

  const totalHlaDistance = useMemo(() => {
    return hlaStore.ways.reduce((acc, hlaWay) => acc + hlaWay.lengthM, 0);
  }, [hlaStore.ways]);

  const selectedStage = selectedOsaWay?.lifeCycleStage;
  const codingNotDone =
    selectedStage === LifeCycleStage.Design ||
    selectedStage === LifeCycleStage.DataCollection ||
    selectedStage === LifeCycleStage.Coding;

  const DisplayDistance = ({
    filtered,
    total,
  }: {
    filtered: number;
    total: number;
  }) => (
    <Box>
      <Text fontSize="xs" fontWeight="semibold">
        {t("map:filters.distanceLabel")}
      </Text>

      <Text fontSize="sm">
        {t("map:filters.distanceSummary", {
          filtered: formatDistanceByUnit(filtered, distanceUnit),
          total: formatDistanceByUnit(total, distanceUnit),
          unit: distanceUnit === DistanceUnit.Imperial ? "mi" : "km",
        })}
      </Text>
    </Box>
  );

  return (
    <VStack
      overflowY="scroll"
      gap={4}
      py={4}
      px={4}
      alignItems="stretch"
      w={320}
      flexShrink={0}
    >
      <HStack mb={-2}>
        <Text>Filters</Text>
        <Spacer />
        <Button
          size="sm"
          fontSize={"sm"}
          colorPalette="primary"
          variant="ghost"
          onClick={() => {
            filterStore.resetFilters();
          }}
          title={t(`map:filters.clearAllTitle`)}
          disabled={
            isScoreFiltersEmpty(filterStore.craScoreFilters) &&
            isScoreFiltersEmpty(filterStore.hlaScoreFilters) &&
            !filterStore.craAttributeFilters.length &&
            !filterStore.osaWayFilters.length &&
            !filterStore.lifeCycleStageFilters.length &&
            !filterStore.treatmentFilters.length &&
            !filterStore.hlaVerifiedFilters.length
          }
        >
          {t(`map:filters.clearAllTitle`)}
        </Button>
      </HStack>

      {osaWayStore.ways.length > 0 && (
        <Stack gap={4}>
          <HStack>
            <Switch.Root
              colorPalette={"primary"}
              checked={filterStore.showCra}
              onCheckedChange={({ checked }) => filterStore.setShowCra(checked)}
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.Label>CycleRAP Assessments</Switch.Label>
            </Switch.Root>
          </HStack>

          {filterStore.showCra && (
            <Card.Root>
              <Stack gap={2} p={2}>
                <Stack flexDir={"row"} flexWrap={"wrap"} alignItems={"start"}>
                  <OsaWayFilters />
                  {authStore.isAuthenticated && <LifeCycleStageFilters />}
                  <TreatmentFilters />
                  <CraScoreFilters />
                  <CraAttributeFilters />
                </Stack>

                <DisplayDistance
                  filtered={filterStore.filteredCraDistance}
                  total={totalCraDistance}
                />

                <Separator mx={-2} />

                <Stack gap={0}>
                  <HStack gap={0}>
                    <Text fontSize={"sm"} flexShrink={0}>
                      Color with
                    </Text>

                    <ColorBySelector
                      value={mapStore.colorBy}
                      onChange={(colorBy) =>
                        mapStore.setColorBy({ ...colorBy })
                      }
                    />
                  </HStack>
                  <MultiContextPieChart colorBy={mapStore.colorBy} />
                </Stack>

                <Separator mx={-2} />

                {!codingNotDone ? (
                  <CraForecastControls />
                ) : (
                  <Alert.Root>
                    <Alert.Indicator />
                    <Alert.Description>
                      {t(`map:filters.forecastUnavailable`)}
                    </Alert.Description>
                  </Alert.Root>
                )}
              </Stack>
            </Card.Root>
          )}
        </Stack>
      )}

      {hlaStore.ways.length > 0 && (
        <Stack gap={4}>
          <HStack>
            <Switch.Root
              colorPalette={"primary"}
              checked={filterStore.showHla}
              onCheckedChange={({ checked }) => filterStore.setShowHla(checked)}
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.Label>High-level Assessments</Switch.Label>
            </Switch.Root>
          </HStack>

          {filterStore.showHla && (
            <Card.Root>
              <Stack gap={2} p={2}>
                <Stack flexDir={"row"} flexWrap={"wrap"} alignItems={"start"}>
                  <HlaVerifiedFilters />
                  <HlaScoreFilters />
                  <HlaAttributeFilters />
                </Stack>

                <DisplayDistance
                  filtered={filterStore.filteredHlaDistance}
                  total={totalHlaDistance}
                />

                <Separator mx={-2} />

                <HlaForecastControls />
              </Stack>
            </Card.Root>
          )}
        </Stack>
      )}
    </VStack>
  );
};

export const FiltersBar = observer(FiltersBase);
